import { FileItem, filesService } from "./FilesService";

export interface WeeklyFileStats {
  weekLabel: string;
  filesCount: number;
  totalSizeKB: number;
  startDate: Date;
  endDate: Date;
}

export interface FilesProcessedReport {
  currentMonth: string;
  weeklySummary: WeeklyFileStats[];
  totalFilesProcessed: number;
  totalSizeKB: number;
}

export class UserFilesReportService {
  private static instance: UserFilesReportService;
  private reportData: FilesProcessedReport | null = null;
  private subscribers: ((data: FilesProcessedReport | null) => void)[] = [];

  private constructor() {
    // Initial data load
    this.refreshReportData();
  }

  public static getInstance(): UserFilesReportService {
    if (!UserFilesReportService.instance) {
      UserFilesReportService.instance = new UserFilesReportService();
    }
    return UserFilesReportService.instance;
  }

  // Get the week number of the given date (Monday to Sunday)
  private getWeekNumber(date: Date): number {
    const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    // Convert day of week to Monday-based (0 = Monday, 6 = Sunday)
    const dayOfWeek = (firstDayOfMonth.getDay() + 6) % 7; // Convert Sunday (0) to 6, Monday (1) to 0, etc.
    
    // Adjust to get the first Monday of the first week
    const firstWeekStart = new Date(firstDayOfMonth);
    firstWeekStart.setDate(1 - dayOfWeek);
    
    // Calculate the difference in days
    const diffTime = Math.abs(date.getTime() - firstWeekStart.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    // Calculate the week number (1-indexed)
    return Math.floor(diffDays / 7) + 1;
  }

  // Get week start and end dates (Monday to Sunday)
  private getWeekBoundaries(date: Date, weekNumber: number): { start: Date; end: Date } {
    const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    // Convert day of week to Monday-based (0 = Monday, 6 = Sunday)
    const dayOfWeek = (firstDayOfMonth.getDay() + 6) % 7; // Convert Sunday (0) to 6, Monday (1) to 0, etc.
    
    // Calculate the first Monday of the first week
    const firstWeekStart = new Date(firstDayOfMonth);
    firstWeekStart.setDate(1 - dayOfWeek);
    
    // Calculate start of the target week (starts on Monday)
    const weekStart = new Date(firstWeekStart);
    weekStart.setDate(firstWeekStart.getDate() + (weekNumber - 1) * 7);
    
    // Calculate end of the target week (ends on Sunday)
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    
    return { start: weekStart, end: weekEnd };
  }

  // Format a date range for display
  private formatDateRange(start: Date, end: Date): string {
    try {
      const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
      return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`;
    } catch (error) {
      console.error("Error formatting date range:", error);
      return "Invalid Date";
    }
  }

  private async processFileData(files: FileItem[]): Promise<FilesProcessedReport> {
    // Get the current month and year
    const now = new Date();
    const currentMonth = now.toLocaleString('default', { month: 'long', year: 'numeric' });
    
    // Initialize week statistics
    const weeksInMonth = 5; // Max 5 weeks in a month view
    const weeklyStats: Record<number, WeeklyFileStats> = {};
    
    // Initialize each week's data
    for (let i = 1; i <= weeksInMonth; i++) {
      const { start, end } = this.getWeekBoundaries(now, i);
      weeklyStats[i] = {
        weekLabel: `Week ${i}`,
        filesCount: 0,
        totalSizeKB: 0,
        startDate: start,
        endDate: end
      };
    }
    
    // Process each file
    let totalFilesProcessed = 0;
    let totalSizeKB = 0;
    
    for (const file of files) {
      const processDate = file.lastProcessedDate;
      
      // Skip files processed in a different month
      if (processDate.getMonth() !== now.getMonth() || 
          processDate.getFullYear() !== now.getFullYear()) {
        continue;
      }
      
      // Determine which week this file belongs to
      const weekNumber = this.getWeekNumber(processDate);
      
      // Only count files processed in the current month
      if (weekNumber >= 1 && weekNumber <= weeksInMonth) {
        weeklyStats[weekNumber].filesCount++;
        // Estimate file size if needed (or you can store actual size in your FileItem)
        const estimatedSizeKB = file.pdfRawContent.size / 1024;
        weeklyStats[weekNumber].totalSizeKB += estimatedSizeKB;
        
        totalFilesProcessed++;
        totalSizeKB += estimatedSizeKB;
      }
    }
    
    // Convert weekly stats to array and format date ranges
    const weeklySummary = Object.values(weeklyStats).map(week => {
      return {
        ...week,
        weekLabel: this.formatDateRange(week.startDate, week.endDate)
      };
    });

    return {
      currentMonth,
      weeklySummary,
      totalFilesProcessed,
      totalSizeKB
    };
  }

  public async refreshReportData(): Promise<FilesProcessedReport | null> {
    try {
      const files = await filesService.getAllFiles();
      this.reportData = await this.processFileData(files);
      this.notifySubscribers();
      return this.reportData;
    } catch (error) {
      console.error("Error refreshing file report data:", error);
      return null;
    }
  }

  public getReportData(): FilesProcessedReport | null {
    return this.reportData;
  }

  // Subscribe to changes
  public subscribe(
    callback: (data: FilesProcessedReport | null) => void,
  ): () => void {
    this.subscribers.push(callback);
    callback(this.reportData);

    return () => {
      this.subscribers = this.subscribers.filter((cb) => cb !== callback);
    };
  }

  // Notify all subscribers of changes
  private notifySubscribers(): void {
    for (const callback of this.subscribers) {
      callback(this.reportData);
    }
  }
}

export const userFilesReportService = UserFilesReportService.getInstance();